import React, { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AdminGuard, type AdminRole } from '../guards/adminGuard';
import { AuthGuard } from '../guards/authGuard';

const HomePage = lazy(() => import('../scenes/HomePage.scenes'));
const LoginPage = lazy(() => import('../scenes/LoginPage.scenes'));
const ContactUsPage = lazy(() => import('../scenes/ContactUsPage.scenes'));
const QRScanPage = lazy(() => import('../scenes/QRScanPage.scenes'));
const ServicesPage = lazy(() => import('../scenes/SanginiServicesPage.scenes'));
const BuyJewelleriesPage = lazy(() => import('../scenes/BuyJewelleriesPage.scenes'));
const CheckoutPage = lazy(() => import('../scenes/CheckoutPage.scenes'));
const OrderTrackingPage = lazy(() => import('../scenes/OrderTrackingPage.scenes'));
const PhonePeReturnPage = lazy(() => import('../scenes/PhonePeReturnPage.scenes'));
const UserProfilePage = lazy(() => import('../scenes/UserProfilePage.scenes'));
const PortfolioPage = lazy(() => import('../scenes/PortfolioPage.scenes'));
const SignUpPage = lazy(() => import('../scenes/SignUpPage.scenes'));
const PrivacyPolicy = lazy(() => import('../scenes/PrivacyPolicy.scenes'));
const ForgotPasswordPage = lazy(() => import('../scenes/forgotPassword.scenes'));
const TermsAndConditions = lazy(() => import('../scenes/TermsAndConditon.scenes'));
const AdminPage = lazy(() => import('../apps/admin/AdminPage.scenes'));
const ProductDetail = lazy(() => import('../apps/admin/products/pages/productDetails.page'));
const BillingPage = lazy(() => import('../apps/admin/bills/pages/createBill.page'));
const BillsPage = lazy(() => import('../apps/admin/bills/pages/listBill.page'));
const CustomerPage = lazy(() => import('../apps/admin/customers/pages/addWithListCustomer.page'));
const AddNewCustomerPage = lazy(() => import('../apps/admin/customers/component/addCustomer/addNewCustomer.component'));
const EditCustomerPage = lazy(() => import('../apps/admin/customers/pages/editCustomer.page'));
const RatePage = lazy(() => import('../apps/admin/rate/pages/rate.page'));
const AddProductPage = lazy(() => import('../apps/admin/products/pages/addProduct.page'));
const ProductsListPage = lazy(() => import('../apps/admin/products/pages/listProduct.page'));
const InventoryManagementPage = lazy(() => import('../apps/admin/analytics/pages/inventoryManagement.page').then((module) => ({ default: module.InventoryManagementPage })));
const InventoryCheckupPage = lazy(() => import('../apps/admin/analytics/pages/inventoryCheckup.page').then((module) => ({ default: module.InventoryCheckupPage })));
const CustomerAnalyticsPage = lazy(() => import('../apps/admin/analytics/pages/customerAnalytics.page').then((module) => ({ default: module.CustomerAnalyticsPage })));
const InventoryAnalyticsPage = lazy(() => import('../apps/admin/analytics/pages/inventoryAnalytics.page').then((module) => ({ default: module.InventoryAnalyticsPage })));
const SalesAnalyticsPage = lazy(() => import('../apps/admin/analytics/pages/salesAnalytics.page').then((module) => ({ default: module.SalesAnalyticsPage })));
const GoldHoldersPage = lazy(() => import('../apps/admin/goldHolders/pages/goldHolders.page'));
const WhatsappPage = lazy(() => import('../apps/admin/whatsapp/pages/whatsapp.page'));
const SilverBillingPage = lazy(() => import('../apps/admin/silver/pages/silverBilling.page'));
const LegacyBillFixPage = lazy(() => import('../apps/admin/legacyFix/legacyBillFix.page').then((m) => ({ default: m.LegacyBillFixPage })));

type AppRouteConfig = {
  path: string;
  element: React.ReactElement;
};

type AdminRouteConfig = {
  path?: string;
  index?: boolean;
  allowedRoles: AdminRole[];
  element: React.ReactElement;
};

const publicRoutes: AppRouteConfig[] = [
  { path: '/BuyJewelleries', element: <BuyJewelleriesPage /> },
  { path: '/Home', element: <HomePage /> },
  { path: '/login', element: <LoginPage shouldLogout={false} /> },
  { path: '/contactUs', element: <ContactUsPage /> },
  { path: '/GoldRush', element: <QRScanPage /> },
  { path: '/Services', element: <ServicesPage /> },
  { path: '/profile', element: <AuthGuard><UserProfilePage /></AuthGuard> },
  { path: '/portfolio', element: <AuthGuard><PortfolioPage /></AuthGuard> },
  { path: '/checkout', element: <AuthGuard><CheckoutPage /></AuthGuard> },
  { path: '/orders', element: <AuthGuard><OrderTrackingPage /></AuthGuard> },
  { path: '/payment/phonepe/return', element: <AuthGuard><PhonePeReturnPage /></AuthGuard> },
  { path: '/signup', element: <SignUpPage /> },
  { path: '/forgotPassword', element: <ForgotPasswordPage /> },
  { path: '/privacyPolicy', element: <PrivacyPolicy /> },
  { path: '/termsandcondition', element: <TermsAndConditions /> },
  { path: '/product/:id', element: <ProductDetail /> },
  { path: '/', element: <HomePage /> },
];

const adminRoutes: AdminRouteConfig[] = [
  { index: true, allowedRoles: ['isStaff'], element: <ProductsListPage /> },
  { path: 'billing', allowedRoles: ['isStaff'], element: <BillingPage /> },
  { path: 'newCustomer', allowedRoles: ['isStaff'], element: <AddNewCustomerPage /> },
  { path: 'addProduct', allowedRoles: ['isStaff'], element: <AddProductPage /> },
  { path: 'products', allowedRoles: ['isStaff'], element: <ProductsListPage /> },
  { path: 'editProduct/:id', allowedRoles: ['isStaff'], element: <AddProductPage /> },
  { path: 'customer', allowedRoles: ['isManager'], element: <CustomerPage /> },
  { path: 'editcustomer', allowedRoles: ['isManager'], element: <EditCustomerPage /> },
  { path: 'bills', allowedRoles: ['isManager'], element: <BillsPage /> },
  { path: 'bills/:id', allowedRoles: ['isManager'], element: <BillsPage /> },
  { path: 'inventoryms', allowedRoles: ['isOwner'], element: <InventoryManagementPage /> },
  { path: 'inventorycheckup', allowedRoles: ['isManager', 'isAdmin', 'isStaff', 'isOwner'], element: <InventoryCheckupPage /> },
  { path: 'customerAnalytics', allowedRoles: ['isOwner'], element: <CustomerAnalyticsPage /> },
  { path: 'inventoryAnalytics', allowedRoles: ['isOwner'], element: <InventoryAnalyticsPage /> },
  { path: 'salesAnalytics', allowedRoles: ['isOwner'], element: <SalesAnalyticsPage /> },
  { path: 'silverBilling', allowedRoles: ['isManager', 'isStaff'], element: <SilverBillingPage /> },
  { path: 'rate', allowedRoles: ['isAdmin'], element: <RatePage /> },
  { path: 'goldholders', allowedRoles: ['isAdmin', 'isOwner'], element: <GoldHoldersPage /> },
  { path: 'whatsapp', allowedRoles: ['isAdmin', 'isOwner'], element: <WhatsappPage /> },
  { path: 'legacybillfix', allowedRoles: ['isAdmin'], element: <LegacyBillFixPage /> },
];

export function AppRoutes() {
  return (
    <Routes>
      {publicRoutes.map((route) => (
        <Route key={route.path} path={route.path} element={route.element} />
      ))}
      <Route path="/Admin">
        {adminRoutes.map((route) => (
          <Route
            key={route.path ?? 'index'}
            element={
              <AdminGuard allowedRoles={route.allowedRoles}>
                <AdminPage />
              </AdminGuard>
            }
          >
            {route.index ? (
              <Route index element={route.element} />
            ) : (
              <Route path={route.path} element={route.element} />
            )}
          </Route>
        ))}
      </Route>
    </Routes>
  );
}
